import React, { useState } from "react";

// ─── SAMPLE DATA (mirrors the JSON schema from the spec) ─────────────────────

const SKILLS_META = {
  effective_communication: { id: "effective_communication", name: "Effective Communication", skillNum: 8, color: "#2563EB" },
  clear_writing: { id: "clear_writing", name: "Clear Writing", skillNum: 9, color: "#7C3AED" },
  constructing_viable_arguments: { id: "constructing_viable_arguments", name: "Constructing Viable Arguments", skillNum: 3, color: "#059669" },
  precision: { id: "precision", name: "Precision", skillNum: 5, color: "#D97706" },
};

const sampleStudentResponse = {
  assessment_id: "assess_20260115_0042",
  student_id: "STU-7291",
  assignment_id: "assign_math_explain_456",
  timestamp: "2026-01-15T10:45:00Z",
  assignment_context: {
    prompt: "Explain why the area of a triangle is half the area of a rectangle with the same base and height. Use diagrams or examples to support your reasoning.",
    subject: "math",
    grade_level: "7",
    assignment_type: "explanation",
  },
  artifacts_ingested: [
    {
      artifact_id: "art_001",
      type: "written",
      format: "text/plain",
      label: "Written Explanation",
      description: "3-paragraph typed response to assignment prompt",
      word_count: 247,
      skills_sourced: ["constructing_viable_arguments", "effective_communication", "clear_writing", "precision"],
    },
    {
      artifact_id: "art_002",
      type: "video",
      format: "video/mp4",
      label: "Video Walkthrough",
      description: "Student-recorded screen capture explaining their diagram and reasoning process",
      duration_seconds: 138,
      transcript_available: true,
      skills_sourced: ["effective_communication", "precision", "constructing_viable_arguments"],
    },
    {
      artifact_id: "art_003",
      type: "audio",
      format: "audio/webm",
      label: "Verbal Reflection",
      description: "Audio response to prompt: 'What was hardest about this problem and how did you work through it?'",
      duration_seconds: 62,
      transcript_available: true,
      skills_sourced: ["effective_communication", "clear_writing"],
    },
    {
      artifact_id: "art_004",
      type: "produced_work",
      format: "image/png",
      label: "Hand-Drawn Diagram",
      description: "Uploaded photo of hand-drawn rectangle with diagonal, labeled dimensions",
      skills_sourced: ["precision", "constructing_viable_arguments"],
    },
  ],
  skills_assessed: [
    {
      skill_id: "constructing_viable_arguments",
      skill_display_name: "Constructing Viable Arguments",
      score: 3,
      max_score: 4,
      confidence: "high",
      outcomes: [
        { outcome: "Understand Logical Reasoning", score: 3, indicators_detected: ["claim_present", "logical_progression", "evidence_cited"] },
        { outcome: "Construct Arguments", score: 3, indicators_detected: ["claim_evidence_chain", "logical_connectors"] },
        { outcome: "Diverse Problem-Solving", score: 2, indicators_detected: ["single_approach"] },
      ],
      evidence: [
        { indicator: "claim_present", location: "paragraph_1", text: "Student opens with a clear claim about the geometric relationship" },
        { indicator: "evidence_cited", location: "paragraph_2", text: "References diagram showing rectangle divided diagonally" },
        { indicator: "logical_connector", location: "paragraph_3", text: "Uses 'therefore' and 'because' to link reasoning steps" },
        { indicator: "single_approach", location: "full_response", text: "Only one method of proof attempted; no alternative approaches" },
      ],
      feedback_text: "Your argument includes a clear claim and supporting evidence with a diagram reference. To move from a 3 to a 4, try showing the relationship a second way — for example, with a numerical example alongside the geometric proof.",
      teacher_guidance: {
        priority: "high",
        instructional_focus: "Multiple Representations",
        recommendation: "This student builds strong single-method arguments but defaults to one proof strategy. Introduce a 'Two Ways' protocol: after completing an initial explanation, students must demonstrate the same concept through a different modality (numerical, graphical, verbal, or algebraic). Use the video artifact to assess whether the student articulates alternative reasoning verbally even when they don't write it.",
        suggested_activities: [
          "Gallery walk comparing different proof approaches for the same theorem",
          "Partner swap: students exchange written proofs and must explain their partner's method in a recorded audio response",
          "Revision task: return this assignment with the prompt 'Now prove it a completely different way'"
        ],
        rubric_move: "Score 3 → 4 requires evidence of diverse problem-solving approaches. Current work shows only geometric proof.",
      },
    },
    {
      skill_id: "effective_communication",
      skill_display_name: "Effective Communication",
      score: 3,
      max_score: 4,
      confidence: "high",
      outcomes: [
        { outcome: "Compose Texts", score: 3, indicators_detected: ["complete_structured_response", "paragraph_organization"] },
        { outcome: "Sentence Formation", score: 3, indicators_detected: ["syntactic_variety", "compound_sentences"] },
        { outcome: "Grammatical Accuracy", score: 4, indicators_detected: ["grammar_correct", "spelling_correct"] },
      ],
      evidence: [
        { indicator: "complete_structured_response", location: "full_response", text: "Three clear paragraphs with introduction, explanation, and conclusion" },
        { indicator: "syntactic_variety", location: "paragraphs_1_2", text: "Mix of simple and compound sentence structures" },
        { indicator: "grammar_correct", location: "full_response", text: "No grammatical errors detected" },
      ],
      feedback_text: "Strong writing mechanics with clear paragraph structure and varied sentences. Grammar and spelling are excellent.",
      teacher_guidance: {
        priority: "low",
        instructional_focus: "Enrichment — Register Shifting",
        recommendation: "This student has strong foundational communication skills. Push toward register awareness: can they shift their explanation for different audiences (peer vs. teacher vs. younger student)? Use the audio artifact to assess whether verbal communication matches written quality, or if there's a gap between modalities.",
        suggested_activities: [
          "Record a 60-second explanation of the same concept for a 4th grader",
          "Write the same argument as a formal proof vs. an informal blog post"
        ],
        rubric_move: "Already near ceiling on mechanics. Growth edge is audience awareness and cross-modal consistency.",
      },
    },
    {
      skill_id: "precision",
      skill_display_name: "Precision",
      score: 2,
      max_score: 4,
      confidence: "medium",
      outcomes: [
        { outcome: "Accuracy & Precision", score: 2, indicators_detected: ["notation_present"] },
        { outcome: "Communicating Clearly", score: 3, indicators_detected: ["explanation_clarity"] },
        { outcome: "Effective Reasoning", score: 2, indicators_detected: ["partial_step_detection"] },
      ],
      evidence: [
        { indicator: "notation_present", location: "paragraph_2", text: "Uses 'A = b × h' but does not define variables explicitly" },
        { indicator: "partial_step_detection", location: "paragraph_2", text: "Jumps from formula to conclusion without showing intermediate calculation" },
        { indicator: "explanation_clarity", location: "paragraph_1", text: "Verbal explanation is clear even where mathematical notation is imprecise" },
      ],
      feedback_text: "Your verbal explanation is clear, but your mathematical notation needs more precision. Define your variables (What does 'b' represent? What does 'h' represent?) and show each step of your calculation.",
      teacher_guidance: {
        priority: "high",
        instructional_focus: "Mathematical Notation & Variable Definition",
        recommendation: "This is a common gap where students understand concepts verbally but lack the discipline of formal mathematical communication. The video walkthrough confirms the student can reason through the steps aloud, but skips notation in written work. Provide a structured template requiring explicit variable definitions before any formula use. This is not a comprehension issue — it is a precision habit that needs scaffolding.",
        suggested_activities: [
          "Variable definition warm-ups: before solving, students must write 'Let b = base length in cm' for every variable",
          "Peer notation audit: partners check each other's written work for undefined variables",
          "Side-by-side task: compare the student's video explanation (which includes verbal definitions) with their written work (which omits them) and discuss the gap"
        ],
        rubric_move: "Score 2 → 3 requires explicit variable definitions and at least one intermediate calculation step shown.",
      },
    },
    {
      skill_id: "clear_writing",
      skill_display_name: "Clear Writing",
      score: 3,
      max_score: 4,
      confidence: "high",
      outcomes: [
        { outcome: "Effective Language Use", score: 3, indicators_detected: ["vocabulary_appropriate", "coherent_structure"] },
        { outcome: "Domain Vocabulary", score: 2, indicators_detected: ["some_domain_terms"] },
        { outcome: "Effective Communication", score: 3, indicators_detected: ["coherence_high"] },
      ],
      evidence: [
        { indicator: "vocabulary_appropriate", location: "full_response", text: "Grade-appropriate vocabulary used consistently" },
        { indicator: "some_domain_terms", location: "paragraphs_1_3", text: "Uses 'area' and 'triangle' but misses opportunities for 'diagonal', 'congruent', 'base', 'height'" },
        { indicator: "coherence_high", location: "full_response", text: "Ideas flow logically from paragraph to paragraph" },
      ],
      feedback_text: "Your writing is clear and well-organized. To strengthen it, incorporate more mathematical vocabulary — words like 'congruent,' 'diagonal,' and 'base' would make your explanation more precise.",
      teacher_guidance: {
        priority: "medium",
        instructional_focus: "Domain-Specific Academic Vocabulary",
        recommendation: "This student writes coherently but underuses mathematical vocabulary, relying on everyday language where precise terms exist. This is a vocabulary integration issue, not a comprehension gap — the student likely knows words like 'congruent' and 'diagonal' but doesn't deploy them in written explanations. Build a domain vocabulary wall and require 3+ domain terms per written response.",
        suggested_activities: [
          "Vocabulary upgrade exercise: highlight everyday words in their draft, then replace each with the precise mathematical term",
          "Domain word bank: provide 8-10 key terms per unit and require students to use at least 3 in every written explanation",
          "Audio glossary: student records definitions and example sentences for each domain term, building a personal reference library"
        ],
        rubric_move: "Score 2 → 3 on Domain Vocabulary requires consistent use of subject-specific terminology in context.",
      },
    },
  ],
  flags: {
    human_review_recommended: false,
    insufficient_data_skills: [],
  },
};

const sampleClassAggregate = {
  aggregation_level: "class",
  aggregation_id: "class_7B_martinez",
  teacher: "Ms. Martinez",
  class_name: "7th Grade Math — Period 3",
  student_count: 28,
  time_period: "2026-01-01 to 2026-01-31",
  skill_summary: [
    {
      skill_id: "constructing_viable_arguments",
      average_score: 2.7,
      max_score: 4,
      score_distribution: [3, 7, 12, 6],
      growth_trend: "+0.3",
      students_below_proficiency: 10,
      students_at_proficiency: 12,
      students_above_proficiency: 6,
      teacher_guidance: "10 students below proficiency are primarily missing evidence citation and logical connectors. Consider a whole-class mini-lesson on the Claim-Evidence-Reasoning (CER) framework, followed by peer argument audits using a structured checklist.",
    },
    {
      skill_id: "effective_communication",
      average_score: 3.1,
      max_score: 4,
      score_distribution: [1, 5, 14, 8],
      growth_trend: "+0.1",
      students_below_proficiency: 6,
      students_at_proficiency: 14,
      students_above_proficiency: 8,
      teacher_guidance: "Class communication skills are strong. The 6 below-proficiency students primarily struggle with paragraph organization. Small-group writing conferences targeting structure (introduction-body-conclusion) would be more effective than whole-class instruction here.",
    },
    {
      skill_id: "precision",
      average_score: 2.2,
      max_score: 4,
      score_distribution: [5, 10, 9, 4],
      growth_trend: "-0.1",
      students_below_proficiency: 15,
      students_at_proficiency: 9,
      students_above_proficiency: 4,
      teacher_guidance: "Precision is trending downward and 15 students are below proficiency. This is the highest-leverage intervention point for this class. Most common gap: students skip variable definitions and intermediate steps. Implement a mandatory 'Define-Compute-Check' template for all written mathematical work for the next 2 weeks.",
    },
    {
      skill_id: "clear_writing",
      average_score: 2.9,
      max_score: 4,
      score_distribution: [2, 8, 13, 5],
      growth_trend: "+0.2",
      students_below_proficiency: 10,
      students_at_proficiency: 13,
      students_above_proficiency: 5,
      teacher_guidance: "Domain vocabulary is the primary gap across the class. Post a unit-specific word wall and require students to use a minimum of 3 domain terms per written response. Consider vocabulary warm-ups at the start of each class using key mathematical terms in context.",
    },
  ],
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

const ConfidenceBadge = ({ level }) => {
  const styles = {
    high: { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
    medium: { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
    low: { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
  };
  const s = styles[level] || styles.medium;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: "99px",
      fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase",
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {level}
    </span>
  );
};

const ScoreBar = ({ score, max, color }) => {
  const pct = (score / max) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ flex: 1, height: "8px", borderRadius: "4px", background: "#E5E7EB", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: "4px", background: color, transition: "width 0.6s ease" }} />
      </div>
      <span style={{ fontSize: "14px", fontWeight: 700, color: "#1F2937", fontVariantNumeric: "tabular-nums", minWidth: "32px" }}>
        {score}/{max}
      </span>
    </div>
  );
};

const DistributionBar = ({ distribution, color }) => {
  const total = distribution.reduce((a, b) => a + b, 0);
  const labels = ["1", "2", "3", "4"];
  return (
    <div style={{ display: "flex", gap: "3px", alignItems: "flex-end", height: "48px" }}>
      {distribution.map((count, i) => {
        const h = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
            <span style={{ fontSize: "10px", color: "#6B7280", fontWeight: 600 }}>{count}</span>
            <div style={{
              width: "100%", borderRadius: "3px 3px 0 0", minHeight: "4px",
              height: `${Math.max(h * 0.4, 4)}px`,
              background: `${color}${i < 2 ? "40" : i === 2 ? "90" : "FF"}`,
              transition: "height 0.4s ease",
            }} />
            <span style={{ fontSize: "9px", color: "#9CA3AF" }}>{labels[i]}</span>
          </div>
        );
      })}
    </div>
  );
};

const EvidenceItem = ({ ev, color }) => (
  <div style={{
    display: "flex", gap: "10px", padding: "10px 12px",
    background: "#FAFAFA", borderRadius: "6px", borderLeft: `3px solid ${color}`,
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
        <code style={{
          fontSize: "10px", padding: "1px 6px", borderRadius: "3px",
          background: `${color}18`, color: color, fontWeight: 600,
        }}>
          {ev.indicator}
        </code>
        <span style={{ fontSize: "10px", color: "#9CA3AF" }}>{ev.location}</span>
      </div>
      <p style={{ margin: 0, fontSize: "13px", color: "#374151", lineHeight: 1.5 }}>{ev.text}</p>
    </div>
  </div>
);

const SkillCard = ({ skill, meta, expanded, onToggle }) => {
  const color = meta.color;
  return (
    <div style={{
      border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden",
      background: "#FFF", transition: "box-shadow 0.2s",
      boxShadow: expanded ? `0 0 0 2px ${color}30, 0 4px 12px rgba(0,0,0,0.06)` : "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div
        onClick={onToggle}
        style={{
          padding: "16px 20px", cursor: "pointer", display: "flex",
          alignItems: "center", gap: "16px", userSelect: "none",
        }}
      >
        <div style={{
          width: "36px", height: "36px", borderRadius: "8px", background: `${color}14`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", fontWeight: 800, color: color, flexShrink: 0,
        }}>
          {meta.skillNum}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#111827", marginBottom: "6px" }}>
            {skill.skill_display_name}
          </div>
          <ScoreBar score={skill.score} max={skill.max_score} color={color} />
        </div>
        <ConfidenceBadge level={skill.confidence} />
        <span style={{
          fontSize: "18px", color: "#9CA3AF", transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}>▾</span>
      </div>

      {expanded && (
        <div style={{ padding: "0 20px 20px", borderTop: "1px solid #F3F4F6" }}>
          {/* Outcomes */}
          <div style={{ marginTop: "16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
              Rubric Outcomes
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {skill.outcomes.map((o, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "13px", color: "#374151", flex: 1 }}>{o.outcome}</span>
                  <div style={{ display: "flex", gap: "3px" }}>
                    {[1, 2, 3, 4].map(n => (
                      <div key={n} style={{
                        width: "18px", height: "18px", borderRadius: "4px",
                        background: n <= o.score ? color : `${color}15`,
                        transition: "background 0.2s",
                      }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Evidence */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
              Evidence Citations
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {skill.evidence.map((ev, i) => <EvidenceItem key={i} ev={ev} color={color} />)}
            </div>
          </div>

          {/* Feedback */}
          <div style={{
            padding: "14px 16px", borderRadius: "8px",
            background: `${color}08`, border: `1px solid ${color}20`,
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: color, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" }}>
              Student Feedback
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
              {skill.feedback_text}
            </p>
          </div>

          {/* Teacher Guidance */}
          {skill.teacher_guidance && (
            <div style={{
              marginTop: "12px", padding: "16px 18px", borderRadius: "8px",
              background: "#FFFBEB", border: "1px solid #FDE68A",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ fontSize: "15px" }}>&#9881;</span>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Teacher Guidance
                </div>
                <span style={{
                  marginLeft: "auto", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "99px",
                  background: skill.teacher_guidance.priority === "high" ? "#FEE2E2" : skill.teacher_guidance.priority === "medium" ? "#FEF3C7" : "#D1FAE5",
                  color: skill.teacher_guidance.priority === "high" ? "#991B1B" : skill.teacher_guidance.priority === "medium" ? "#92400E" : "#065F46",
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>
                  {skill.teacher_guidance.priority} priority
                </span>
              </div>
              <div style={{
                fontSize: "12px", fontWeight: 700, color: "#78350F", marginBottom: "6px",
              }}>
                Focus: {skill.teacher_guidance.instructional_focus}
              </div>
              <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#451A03", lineHeight: 1.6 }}>
                {skill.teacher_guidance.recommendation}
              </p>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "8px" }}>
                Suggested Activities
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {skill.teacher_guidance.suggested_activities.map((act, i) => (
                  <div key={i} style={{
                    display: "flex", gap: "8px", alignItems: "flex-start",
                    padding: "8px 12px", background: "#FEF3C7", borderRadius: "6px",
                    fontSize: "12px", color: "#451A03", lineHeight: 1.5,
                  }}>
                    <span style={{ fontWeight: 700, color: "#B45309", flexShrink: 0 }}>{i + 1}.</span>
                    <span>{act}</span>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: "10px", padding: "8px 12px", borderRadius: "6px",
                background: "#FFFDE7", border: "1px dashed #D97706",
                fontSize: "12px", color: "#78350F", fontWeight: 600,
              }}>
                Rubric Move: {skill.teacher_guidance.rubric_move}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AggregateSkillRow = ({ summary, meta }) => {
  const color = meta.color;
  const pct = (summary.average_score / summary.max_score) * 100;
  const trendPositive = summary.growth_trend.startsWith("+");
  return (
    <div style={{
      padding: "18px 20px", background: "#FFF", borderRadius: "10px",
      border: "1px solid #E5E7EB", display: "grid",
      gridTemplateColumns: "200px 1fr 120px 100px 80px",
      alignItems: "center", gap: "16px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{
          width: "32px", height: "32px", borderRadius: "7px", background: `${color}14`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "13px", fontWeight: 800, color: color,
        }}>
          {meta.skillNum}
        </div>
        <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>{meta.name}</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ flex: 1, height: "8px", borderRadius: "4px", background: "#E5E7EB", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", borderRadius: "4px", background: color, transition: "width 0.6s ease" }} />
        </div>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#1F2937", minWidth: "40px", fontVariantNumeric: "tabular-nums" }}>
          {summary.average_score.toFixed(1)}/4
        </span>
      </div>

      <DistributionBar distribution={summary.score_distribution} color={color} />

      <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
        <span style={{
          fontSize: "11px", padding: "2px 8px", borderRadius: "4px", fontWeight: 600,
          background: "#FEF2F2", color: "#991B1B",
        }}>{summary.students_below_proficiency}</span>
        <span style={{
          fontSize: "11px", padding: "2px 8px", borderRadius: "4px", fontWeight: 600,
          background: "#FFFBEB", color: "#92400E",
        }}>{summary.students_at_proficiency}</span>
        <span style={{
          fontSize: "11px", padding: "2px 8px", borderRadius: "4px", fontWeight: 600,
          background: "#ECFDF5", color: "#065F46",
        }}>{summary.students_above_proficiency}</span>
      </div>

      <div style={{
        fontSize: "14px", fontWeight: 700, textAlign: "center",
        color: trendPositive ? "#059669" : "#DC2626",
      }}>
        {summary.growth_trend}
      </div>
    </div>
  );
};

// ─── JSON VIEWER ──────────────────────────────────────────────────────────────

const JsonViewer = ({ data, label }) => (
  <div style={{ marginTop: "12px" }}>
    <div style={{
      fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase",
      letterSpacing: "0.8px", marginBottom: "8px",
    }}>{label}</div>
    <pre style={{
      background: "#111827", color: "#D1D5DB", padding: "20px",
      borderRadius: "10px", fontSize: "12px", lineHeight: 1.6,
      overflow: "auto", maxHeight: "400px", margin: 0,
      fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
    }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  </div>
);

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function MindsetsDashboard() {
  const [view, setView] = useState("student");
  const [expandedSkill, setExpandedSkill] = useState("constructing_viable_arguments");
  const [showJson, setShowJson] = useState(false);

  const tabStyle = (active) => ({
    padding: "8px 20px", borderRadius: "8px", border: "none", cursor: "pointer",
    fontSize: "13px", fontWeight: 600, transition: "all 0.2s",
    background: active ? "#111827" : "transparent",
    color: active ? "#FFF" : "#6B7280",
  });

  return (
    <div style={{
      fontFamily: "'Instrument Sans', 'DM Sans', 'Segoe UI', system-ui, sans-serif",
      background: "#F8F9FB", minHeight: "100vh", padding: "0",
    }}>
      {/* Header */}
      <div style={{
        background: "#111827",
        padding: "28px 32px 20px",
        borderBottom: "3px solid #2563EB",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
          <div>
            <div style={{
              fontSize: "10px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase",
              color: "#60A5FA", marginBottom: "6px",
            }}>
              Mindsets Intelligence Engine
            </div>
            <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#F9FAFB", letterSpacing: "-0.3px" }}>
              Future-Ready Skills — API Output Preview
            </h1>
          </div>
          <div style={{
            padding: "4px 14px", borderRadius: "6px", border: "1px solid #374151",
            fontSize: "11px", fontWeight: 600, color: "#9CA3AF",
          }}>
            MVP Phase 1 · 4 Skills
          </div>
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#9CA3AF", lineHeight: 1.5 }}>
          This is what partner platforms receive from the API. Every score is grounded in detected evidence from student work.
        </p>
      </div>

      {/* Controls */}
      <div style={{
        padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #E5E7EB", background: "#FFF",
      }}>
        <div style={{ display: "flex", gap: "4px", background: "#F3F4F6", padding: "3px", borderRadius: "10px" }}>
          <button style={tabStyle(view === "student")} onClick={() => setView("student")}>
            Student Assessment
          </button>
          <button style={tabStyle(view === "aggregate")} onClick={() => setView("aggregate")}>
            Class Aggregate
          </button>
        </div>
        <button
          onClick={() => setShowJson(!showJson)}
          style={{
            padding: "7px 16px", borderRadius: "7px", border: "1px solid #D1D5DB",
            background: showJson ? "#111827" : "#FFF", color: showJson ? "#FFF" : "#374151",
            fontSize: "12px", fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
          }}
        >
          {showJson ? "◉" : "○"} Raw JSON
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 32px", maxWidth: "960px" }}>
        {view === "student" ? (
          <>
            {/* Student context */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "12px", marginBottom: "20px",
            }}>
              <div style={{ padding: "14px 18px", background: "#FFF", borderRadius: "8px", border: "1px solid #E5E7EB" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
                  Student
                </div>
                <div style={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>
                  {sampleStudentResponse.student_id}
                </div>
                <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
                  Grade {sampleStudentResponse.assignment_context.grade_level} · {sampleStudentResponse.assignment_context.subject.toUpperCase()}
                </div>
              </div>
              <div style={{ padding: "14px 18px", background: "#FFF", borderRadius: "8px", border: "1px solid #E5E7EB" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
                  Assignment Prompt
                </div>
                <div style={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}>
                  {sampleStudentResponse.assignment_context.prompt}
                </div>
              </div>
            </div>

            {/* Artifacts Ingested */}
            <div style={{
              marginBottom: "20px", padding: "16px 18px", background: "#FFF",
              borderRadius: "10px", border: "1px solid #E5E7EB",
            }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "12px" }}>
                Artifacts Ingested
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {sampleStudentResponse.artifacts_ingested.map((art) => {
                  const typeStyles = {
                    written: { icon: "&#9998;", bg: "#EFF6FF", border: "#BFDBFE", color: "#1E40AF" },
                    video: { icon: "&#9654;", bg: "#F5F3FF", border: "#DDD6FE", color: "#5B21B6" },
                    audio: { icon: "&#9835;", bg: "#FDF2F8", border: "#FBCFE8", color: "#9D174D" },
                    produced_work: { icon: "&#9998;", bg: "#ECFDF5", border: "#A7F3D0", color: "#065F46" },
                  };
                  const ts = typeStyles[art.type] || typeStyles.written;
                  return (
                    <div key={art.artifact_id} style={{
                      display: "flex", gap: "12px", padding: "12px 14px",
                      background: ts.bg, borderRadius: "8px", border: `1px solid ${ts.border}`,
                      alignItems: "flex-start",
                    }}>
                      <span style={{ fontSize: "18px", flexShrink: 0, lineHeight: 1 }} dangerouslySetInnerHTML={{ __html: ts.icon }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: ts.color }}>{art.label}</span>
                          <span style={{
                            fontSize: "9px", fontWeight: 700, padding: "1px 6px", borderRadius: "3px",
                            background: `${ts.color}15`, color: ts.color, textTransform: "uppercase", letterSpacing: "0.5px",
                          }}>{art.type.replace("_", " ")}</span>
                        </div>
                        <div style={{ fontSize: "11px", color: "#6B7280", lineHeight: 1.4, marginBottom: "4px" }}>
                          {art.description}
                        </div>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", fontSize: "10px", color: "#9CA3AF" }}>
                          {art.word_count && <span>{art.word_count} words</span>}
                          {art.duration_seconds && <span>{Math.floor(art.duration_seconds / 60)}:{String(art.duration_seconds % 60).padStart(2, "0")} duration</span>}
                          {art.transcript_available && <span style={{ color: "#059669" }}>transcript available</span>}
                          <span>{art.format}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{
                marginTop: "10px", padding: "8px 14px", background: "#F9FAFB", borderRadius: "6px",
                fontSize: "11px", color: "#6B7280", lineHeight: 1.5,
              }}>
                The Intelligence Engine cross-references all submitted artifacts. Skill scores reflect composite analysis across written, video, audio, and produced work — not any single artifact in isolation.
              </div>
            </div>

            {/* Flags bar */}
            <div style={{
              display: "flex", gap: "16px", padding: "10px 18px", marginBottom: "20px",
              background: sampleStudentResponse.flags.human_review_recommended ? "#FEF2F2" : "#F0FDF4",
              borderRadius: "8px", fontSize: "12px", fontWeight: 600, alignItems: "center",
              color: sampleStudentResponse.flags.human_review_recommended ? "#991B1B" : "#166534",
            }}>
              <span>{sampleStudentResponse.flags.human_review_recommended ? "⚠" : "✓"}</span>
              <span>
                {sampleStudentResponse.flags.human_review_recommended
                  ? "Human review recommended"
                  : "No flags — all skills assessed with sufficient data"}
              </span>
            </div>

            {/* Skill cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {sampleStudentResponse.skills_assessed.map((skill) => (
                <SkillCard
                  key={skill.skill_id}
                  skill={skill}
                  meta={SKILLS_META[skill.skill_id]}
                  expanded={expandedSkill === skill.skill_id}
                  onToggle={() => setExpandedSkill(expandedSkill === skill.skill_id ? null : skill.skill_id)}
                />
              ))}
            </div>

            {showJson && <JsonViewer data={sampleStudentResponse} label="Per-Assignment API Response (JSON)" />}
          </>
        ) : (
          <>
            {/* Class header */}
            <div style={{
              padding: "16px 20px", background: "#FFF", borderRadius: "10px",
              border: "1px solid #E5E7EB", marginBottom: "20px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                  {sampleClassAggregate.class_name}
                </div>
                <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>
                  {sampleClassAggregate.teacher} · {sampleClassAggregate.student_count} students · {sampleClassAggregate.time_period}
                </div>
              </div>
              <div style={{
                padding: "6px 14px", borderRadius: "8px", background: "#F3F4F6",
                fontSize: "11px", fontWeight: 600, color: "#6B7280",
              }}>
                Aggregation: {sampleClassAggregate.aggregation_level}
              </div>
            </div>

            {/* Column headers */}
            <div style={{
              display: "grid", gridTemplateColumns: "200px 1fr 120px 100px 80px",
              gap: "16px", padding: "0 20px 8px", fontSize: "10px", fontWeight: 700,
              color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.8px",
            }}>
              <span>Skill</span>
              <span>Class Average</span>
              <span style={{ textAlign: "center" }}>Distribution</span>
              <span style={{ textAlign: "center" }}>Proficiency</span>
              <span style={{ textAlign: "center" }}>Trend</span>
            </div>

            {/* Rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {sampleClassAggregate.skill_summary.map((summary) => (
                <AggregateSkillRow
                  key={summary.skill_id}
                  summary={summary}
                  meta={SKILLS_META[summary.skill_id]}
                />
              ))}
            </div>

            {/* Class Teacher Guidance */}
            <div style={{
              marginTop: "16px", padding: "18px 20px", background: "#FFFBEB",
              borderRadius: "10px", border: "1px solid #FDE68A",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                <span style={{ fontSize: "16px" }}>&#9881;</span>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Teacher Action Items — Class Level
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {sampleClassAggregate.skill_summary.map((summary) => {
                  const meta = SKILLS_META[summary.skill_id];
                  const isNegative = summary.growth_trend.startsWith("-");
                  return (
                    <div key={summary.skill_id} style={{
                      padding: "12px 16px", background: "#FFF", borderRadius: "8px",
                      borderLeft: `4px solid ${isNegative ? "#DC2626" : meta.color}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{meta.name}</span>
                        {isNegative && (
                          <span style={{
                            fontSize: "9px", fontWeight: 700, padding: "1px 6px", borderRadius: "3px",
                            background: "#FEE2E2", color: "#991B1B", textTransform: "uppercase",
                          }}>needs attention</span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: "12px", color: "#451A03", lineHeight: 1.6 }}>
                        {summary.teacher_guidance}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div style={{
              display: "flex", gap: "20px", marginTop: "16px", padding: "12px 20px",
              background: "#FFF", borderRadius: "8px", border: "1px solid #E5E7EB",
              fontSize: "11px", color: "#6B7280",
            }}>
              <span style={{ fontWeight: 700, color: "#374151" }}>Proficiency key:</span>
              <span>
                <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "2px", background: "#FEF2F2", border: "1px solid #FECACA", marginRight: "4px", verticalAlign: "middle" }} />
                Below
              </span>
              <span>
                <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "2px", background: "#FFFBEB", border: "1px solid #FDE68A", marginRight: "4px", verticalAlign: "middle" }} />
                At
              </span>
              <span>
                <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "2px", background: "#ECFDF5", border: "1px solid #A7F3D0", marginRight: "4px", verticalAlign: "middle" }} />
                Above
              </span>
            </div>

            {showJson && <JsonViewer data={sampleClassAggregate} label="Aggregated API Response (JSON)" />}
          </>
        )}
      </div>
    </div>
  );
}
